// @flow

import { connect } from 'react-redux';

import { selectTaxesDisplaySetting } from '@gandi/authentication';
import type { TranslatorProps } from '@gandi/translate';
import { Base, Cell, Text, Popover, PopoverContainer } from '@gandi/ui';

import type { QuotedProduct } from 'src/DomainRenew/componentsNDS/types';

import type { FullState } from '../../../types';

import styles from './PriceWithOptionsCell.scss';

type StateProps = {
  taxesIncluded: boolean,
};

type OwnProps = { quotedProduct: QuotedProduct, translator: TranslatorProps };

type Props = {
  ...OwnProps,
  ...StateProps,
};

const popoverIconModifiers = [
  {
    name: 'flip',
    options: {
      // This means, prefer the bottom placement,
      // but allow repositioning on all sides, not just top.
      allowedAutoPlacements: ['bottom', 'top', 'left', 'right'],
    },
  },
];

function renderOptionRows(options: Array<string>, __: TranslatorProps['__']) {
  const countMailboxes = options.filter(
    option => option === 'mailboxv2',
  ).length;
  const hasTrusteeOption = !!options.find(option => option === 'trustee');
  const hasDiskOption = !!options.find(option => option === 'paas');

  return (
    <>
      {countMailboxes > 0 && (
        <span>{__('%(count)d mailboxes', { count: countMailboxes })}</span>
      )}
      {hasTrusteeOption && <span>{__('Trustee')}</span>}
      {hasDiskOption && <span>{__('storage')}</span>}
    </>
  );
}

function renderGlobalOptionsCount(
  options: Array<string>,
  __: TranslatorProps['__'],
) {
  return __('incl. %(count)d additional services', {
    count: options.length,
  });
}

function PriceWithOptionsCell(props: Props) {
  const {
    translator: { __ },
    translator,
    quotedProduct,
    taxesIncluded,
  } = props;

  const price = Number(
    taxesIncluded
      ? quotedProduct.price?.afterTaxes
      : quotedProduct.price?.beforeTaxes,
  );

  if (Number.isNaN(price)) {
    return null;
  }

  return (
    <Cell>
      <Base>
        <Base className={styles.priceBox}>
          <Base tagName="span">
            {translator.formatPrice(price, quotedProduct.currency)}
          </Base>{' '}
          <Text size="small" color="light">
            {quotedProduct.duration === 1 ||
            (quotedProduct.duration === 12 &&
              quotedProduct.duration_unit === 'm')
              ? `${__('/year')}`
              : // $FlowFixMe
                `${quotedProduct.duration} ${__('years')}`}
          </Text>
        </Base>

        {quotedProduct.options && (
          <PopoverContainer
            id="quoted-product-options"
            behaviour="click"
            renderReference={getReferenceProps => (
              <Base
                size="small"
                color="green"
                placement="bottom"
                modifiers={popoverIconModifiers}
                tabIndex={0}
                role="button"
                className={styles.quotedProductOptions}
                {...getReferenceProps()}
              >
                {
                  // $FlowFixMe presence of `options` is tested above
                  renderGlobalOptionsCount(quotedProduct.options, __)
                }
              </Base>
            )}
          >
            {getPopoverProps => (
              <Popover {...getPopoverProps()}>
                {
                  // $FlowFixMe presence of `options` is tested above
                  renderOptionRows(quotedProduct.options, __)
                }
              </Popover>
            )}
          </PopoverContainer>
        )}
      </Base>
    </Cell>
  );
}

const mapStateToProps = (state: FullState): StateProps => ({
  taxesIncluded: selectTaxesDisplaySetting(state),
});

// $FlowFixMe[signature-verification-failure]
// $FlowFixMe[underconstrained-implicit-instantiation]
export default connect(mapStateToProps)(PriceWithOptionsCell);
